import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import set from 'lodash/set';
import SM from 'services/ServiceManager';
import notification from 'ui-library/components/Notification';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { adaptLists } from '../adapters/adaptLists';
import {
    SortableItemType,
    sortByAlphabet, sortList,
} from '../../../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/helpers/sortList';
import {
    removeListLanguages,
} from '../../../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/helpers/removeListLanguages';
import {
    additionalLanguageList,
} from '../../../pages/ClientOnboardingFlow/pages/NewsPlan/NewPersonalInformationPage/constants/options';

const SCHEMA_LIST_PATH = 'additiv.FormIoCommonDataSource';

type StateType = {
    data: SchemaType|null,
    error: any,
    isLoading: boolean,
    dataLists: Array<SortableItemType>|null,
    errorInSchemaLists: any,
    statusOfSchemaLists: any,
    isLoadingLists: boolean,
    dataListsKey: Array<SortableItemType>|null,
    errorListsKey: any,
    isLoadingListsKey: boolean,
}

type HookResult = StateType & {
    getSchema: ({ key }: SchemaParamsType) => void,
    fetchSchema: <T>(params: SchemaParamsType) => Promise<T>,
    getSchemasLists: <T>() => Promise<T>,
    getSchemaListByKey: <T>(params: SchemaParamsType) => Promise<T>,
    getSchemaLabelById: (itemId: number) => string|null
}

type UseSchemaParamsType = {
    loadInitially?: boolean
    schemaKey?: string
}

type SchemaParamsType = {
    key?: string
}

type SchemaType = {
    concurrencyToken: number,
    id: number,
    jsonSchema: string,
    key: string,
    schema: string
}

export const useSchema = (options: UseSchemaParamsType = {}): HookResult => {
    const { loadInitially = true } = options;
    const { i18n: { language }, t } = useTranslation();

    const [schemaKey, setSchemaKey] = useState<string | undefined>(undefined);
    const [listKey, setListKey] = useState<string | undefined>(undefined);
    const [listKeyError, setListKeyError] = useState<any>(null);

    const {
        data: rawSchemaData,
        error,
        isLoading,
        refetch: refetchSchema,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getSchema',
        payload: schemaKey,
        enabled: !!schemaKey,
        staleTime: 60 * 60 * 1000,
        gcTime: 2 * 60 * 60 * 1000,
    });

    const {
        data: rawListsData,
        error: errorInSchemaLists,
        isLoading: isLoadingLists,
        refetch: refetchLists,
        status: statusOfSchemaLists,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getSchemasLists',
        payload: language,
        enabled: loadInitially,
        staleTime: 60 * 60 * 1000,
        gcTime: 2 * 60 * 60 * 1000,
    });

    // Set window cache + adapt lists when data arrives
    useEffect(() => {
        if (!rawListsData?.data) return;

        const { items } = rawListsData.data;
        const adaptedLists = adaptLists(items);

        adaptedLists.language = removeListLanguages(sortList(adaptedLists.language, additionalLanguageList)).map(m => ({ id: m.id, value: String(m.id), label: m.label }));
        adaptedLists.country = sortByAlphabet(adaptedLists.country);

        set(window, SCHEMA_LIST_PATH, { Lists: adaptedLists, PureList: rawListsData.data, language });
    }, [rawListsData, language]);

    // --- getSchemaListByKey (useQuery directly — needs 2 args, useGetCustomService only passes 1) ---
    const {
        data: rawListsKeyData,
        isLoading: isLoadingListsKey,
        refetch: refetchListByKey,
    } = useQuery({
        queryKey: ['getData_getSchemaListByKey', listKey, language],
        enabled: false,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            try {
                const response = await SM.clientService('getSchemaListByKey', [listKey, language]);

                return response?.data?.items?.[0]?.listItems || [];
            } catch (err: any) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError' || err?.name === 'CanceledError') {
                    return null;
                }
                notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${err.message}`, type: 'error' });
                setListKeyError(err);

                return null;
            }
        },
    });

    const fetchSchema = useCallback(async <T>({ key }: SchemaParamsType = {}): Promise<T> => {
        if (key !== schemaKey) {
            setSchemaKey(key);

            return null as T;
        }

        const result = await refetchSchema();

        return result.data?.data as T;
    }, [schemaKey, refetchSchema]);

    const getSchemasLists = useCallback(async <T>(): Promise<T> => {
        const dataSource = get(window, SCHEMA_LIST_PATH) as any;

        if (dataSource && dataSource?.language === language) {
            return dataSource?.PureList as T;
        }

        const result = await refetchLists();

        return result.data?.data?.items as T;
    }, [language, refetchLists]);

    const getSchemaListByKey = useCallback(async <T>({ key }: SchemaParamsType): Promise<T> => {
        const existKey = get(window, SCHEMA_LIST_PATH)?.Lists?.[key];

        if (existKey) {
            return existKey as T;
        }

        if (key !== listKey) {
            setListKey(key);

            return null as T;
        }

        const result = await refetchListByKey();

        return (result.data || []) as T;
    }, [listKey, refetchListByKey]);

    const getSchemaLabelById = useCallback((itemId: number) => {
        const itemFound = (rawListsData?.data?.items || [])?.find((item) => item?.id === itemId);

        return itemFound?.label || null;
    }, [rawListsData]);

    return {
        data: rawSchemaData?.data ?? null,
        error,
        isLoading,
        getSchema: ({ key }: SchemaParamsType) => setSchemaKey(key),
        fetchSchema,
        dataLists: rawListsData?.data?.items ?? null,
        errorInSchemaLists,
        isLoadingLists,
        statusOfSchemaLists,
        getSchemasLists,
        dataListsKey: rawListsKeyData ?? null,
        errorListsKey: listKeyError,
        isLoadingListsKey,
        getSchemaListByKey,
        getSchemaLabelById,
    };
};
